import { timeoutAfter } from './lf-utils.js';

/** A request that takes longer than this is abandoned and the link simply navigates to the next page. */
const FETCH_TIMEOUT = 15000;

/**
 * "Load more" under a collection grid. The server renders the first page of products plus this element (the counts, a
 * progress bar and a real link to the next page). A click fetches the next page through the Section Rendering API
 * (`?page=N&section_id=<id>`, on top of the current filters and sort), moves its product cards into Horizon's grid and
 * takes the new counts from the same response, so the numbers always come from the server.
 *
 * Nothing is pushed to the history: the URL keeps describing only the filters and the sort. Horizon's facets re-render
 * the whole section after a filter or sort change, which swaps this element for the server's page-1 state; a response
 * that arrives after such a change is dropped. When Horizon's section renderer is loadable its cache is shared, since
 * Horizon prefetches the next page on load. If anything fails the link simply navigates to the next page.
 */
class LfLoadMore extends HTMLElement {
  #busy = false;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    // Horizon swaps this element out when filters or the sort change; the request for the old list must not outlive it.
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-lf-load-more]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    void this.#load(link);
  };

  /**
   * @param {number} page
   * @param {AbortSignal} signal
   */
  async #fetchPage(page, signal) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    const sectionId = this.dataset.sectionId ?? '';

    try {
      const { sectionRenderer } = await import('@theme/section-renderer');
      return await sectionRenderer.getSectionHTML(sectionId, !window.Shopify?.designMode, url, signal);
    } catch {
      // Horizon's renderer is not available or failed: ask for the section directly.
    }

    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url, { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error(`Page ${page} returned ${response.status}`);
    return response.text();
  }

  /** @param {HTMLAnchorElement} link */
  async #load(link) {
    if (this.#busy) return;

    const page = Number(this.dataset.nextPage);
    const grid = this.closest('results-list')?.querySelector('[ref="grid"]');
    if (!page || !grid) {
      window.location.assign(link.href);
      return;
    }

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    const label = link.textContent;
    const hadFocus = document.activeElement === link;
    if (this.dataset.loadingLabel) link.textContent = this.dataset.loadingLabel;
    const search = window.location.search;
    const abort = new AbortController();
    this.#abort = abort;

    try {
      const html = await timeoutAfter(this.#fetchPage(page, abort.signal), FETCH_TIMEOUT);
      // The shopper changed filters or sort while the page was loading: the section was re-rendered from page 1.
      if (!this.isConnected || window.location.search !== search) return;

      const incoming = new DOMParser().parseFromString(html, 'text/html');
      const present = new Set([...grid.children].map((child) => child.id));
      // Cards are imported into this document (the parsed one is never adopted node by node) and any executable <script>
      // is dropped; JSON data scripts stay. Horizon's cards hold none, so this only closes a hardening gap.
      const cards = [...incoming.querySelectorAll('[ref="grid"] > [ref="cards[]"]')]
        .filter((card) => !present.has(card.id))
        .map((card) => document.importNode(card, true));
      if (cards.length === 0) throw new Error(`Page ${page} has no products`);
      for (const card of cards) card.querySelectorAll('script:not([type="application/json"])').forEach((script) => script.remove());
      grid.append(...cards);

      this.#sync(link, incoming.querySelector('lf-load-more'));
      if (hadFocus) {
        // Keyboard focus continues at the first product that was just added, so the shopper does not have to tab back up the grid.
        const firstNew = cards[0].querySelector('a[href]');
        const target = firstNew ?? (link.hidden ? this.querySelector('[data-lf-load-more-status]') : link);
        if (target instanceof HTMLElement) {
          if (target !== link && target !== firstNew) target.tabIndex = -1;
          target.focus({ preventScroll: target === link });
        }
      }
    } catch {
      // Replaced by a filter or sort change (aborted on purpose), or the request failed or timed out: only the latter navigates.
      if (!abort.signal.aborted && this.isConnected) window.location.assign(link.href);
      abort.abort();
    } finally {
      if (this.#abort === abort) this.#abort = null;
      this.#busy = false;
      this.removeAttribute('aria-busy');
      link.textContent = label;
    }
  }

  /**
   * Takes the counts, the progress and the next link from the server's version of this element.
   * @param {HTMLAnchorElement} link
   * @param {Element | null} incoming
   */
  #sync(link, incoming) {
    const next = incoming?.querySelector('a[data-lf-load-more]');
    this.dataset.nextPage = incoming instanceof HTMLElement ? (incoming.dataset.nextPage ?? '') : '';

    // The range starts where this element's first page started (page 1, or the page the shopper landed on), so the text
    // is rebuilt from the server's last/total and this element's own first, never copied from the response.
    const status = this.querySelector('[data-lf-load-more-status]');
    if (status && incoming instanceof HTMLElement) {
      const first = Number(this.dataset.first) || 1;
      const template = (first > 1 ? this.dataset.templatePaged : this.dataset.template) ?? '';
      const text = template
        .replace('{first}', String(first))
        .replace('{last}', incoming.dataset.last ?? '')
        .replace('{total}', incoming.dataset.total ?? '');
      if (text) status.textContent = text;
      this.dataset.last = incoming.dataset.last ?? this.dataset.last ?? '';
      this.dataset.total = incoming.dataset.total ?? this.dataset.total ?? '';
    }

    const progress = incoming?.querySelector('.lf-loadmore__bar');
    if (progress instanceof HTMLElement) {
      this.querySelector('.lf-loadmore__bar')?.style.setProperty('--lf-progress', progress.style.getPropertyValue('--lf-progress'));
    }

    if (next instanceof HTMLAnchorElement && this.dataset.nextPage) {
      const url = new URL(next.getAttribute('href') ?? '', window.location.origin);
      url.searchParams.delete('section_id');
      link.href = `${url.pathname}${url.search}`;
    } else {
      link.hidden = true;
    }
  }
}

if (!customElements.get('lf-load-more')) customElements.define('lf-load-more', LfLoadMore);
